import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { FilestackImageData } from 'api/types/shared.types';
import { PublicPage } from 'api/website';
import { getContentPageUrl } from 'cms/helpers';
import { Box } from 'components/Box';
import { PrimaryButtonLink, SecondaryButtonLink } from 'components/Button';
import { FilestackImage } from 'components/Image';
import { Body, BodySmall, H2, H4, H5, HeadingSmall } from 'components/Text';
import { colors, gutterWidth } from 'theme';
import { getImageSrc } from 'utils/getImageSrc';

const ImageContainer = styled.div`
  margin-bottom: 24px;
  text-align: center;
  img {
    object-fit: contain;
  }
`;
type ImageSize = 'SMALL' | 'LARGE';
type CardData = {
  image?: FilestackImageData | null | undefined;
  label?: string | null | undefined;
  headline?: string | null | undefined;
  subheadline?: string | null | undefined;
  text?: string | null | undefined;
  body?: ReactNode | null | undefined;
  url?: string | null | undefined;
  urls?: Record<UserLocale, string>;
};

export function Card({
  card,
  imageSize,
  maxLength = 3,
}: {
  card: CardData;
  imageSize?: ImageSize | null | undefined;
  maxLength?: number;
}) {
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const url = card.url
    ? card.url
    : card.urls
    ? card.urls[CURRENT_LOCALE]
    : null;
  return (
    <>
      {card.image &&
        (imageSize && imageSize === 'SMALL' ? (
          <ImageContainer>
            <FilestackImage
              data={card.image}
              alt={card.headline || ''}
              height={80}
              width={200}
            />
          </ImageContainer>
        ) : (
          <Box
            width={maxLength === 3 ? 240 : 180}
            mx="auto"
            mb={24}
            height={maxLength === 3 ? 240 : 180}
            borderRadius="50%"
            backgroundImage={`url(${getImageSrc({
              handle: card.image.handle,
              mimetype: card.image.mimetype,
            })})`}
            backgroundSize="cover"
            backgroundPosition="center"
          />
        ))}
      {card.label && (
        <HeadingSmall
          mt={0}
          mb="8px"
          textOverflow="ellipsis"
          overflow="hidden"
          whiteSpace="nowrap"
        >
          {card.label}
        </HeadingSmall>
      )}
      {card.headline && (
        <H4 mt={0} mb={16} lineHeight={1.375}>
          {url ? <a href={url}>{card.headline}</a> : card.headline}
        </H4>
      )}
      {card.subheadline && (
        <H5 mt={0} mb={16} lineHeight={1.375}>
          {card.url ? (
            <a href={card.url}>{card.subheadline}</a>
          ) : (
            card.subheadline
          )}
        </H5>
      )}
      {card.text && (
        <BodySmall
          lineHeight={1.5}
          mt={0}
          mb={16}
          dangerouslySetInnerHTML={{
            __html: card.text,
          }}
        />
      )}
      {card.body && (
        <BodySmall lineHeight={1.5} mt={0}>
          {card.body}
        </BodySmall>
      )}
    </>
  );
}

export function CardContainer({
  headline,
  subheadline,
  buttonText,
  buttonUrl,
  children,
}: {
  headline?: string | null | undefined;
  subheadline?: string | null | undefined;
  buttonText?: string | null | undefined;
  buttonUrl?: string | null | undefined;
  children: ReactNode;
}) {
  return (
    <Box
      mx={[-gutterWidth / 2, null, -gutterWidth]}
      p={[gutterWidth / 2, null, gutterWidth]}
      borderRadius={[0, null, null, 48]}
      bg={colors.fog}
      textAlign="center"
    >
      {headline && (
        <H2
          dangerouslySetInnerHTML={{
            __html: headline,
          }}
        />
      )}
      {subheadline && (
        <Body
          dangerouslySetInnerHTML={{
            __html: subheadline,
          }}
        />
      )}
      {buttonText && buttonUrl && (
        <PrimaryButtonLink href={buttonUrl}>{buttonText}</PrimaryButtonLink>
      )}
      <Box
        display="flex"
        justifyContent="center"
        alignItems={['center', null, 'flex-start']}
        flexWrap="wrap"
        flexDirection={['column', null, 'row']}
        mt={headline || subheadline || (buttonText && buttonUrl) ? 48 : 0}
      >
        {children}
      </Box>
    </Box>
  );
}
type Data = {
  headline?: string | null | undefined;
  subheadline?: string | null | undefined;
  buttonText?: string | null | undefined;
  buttonUrl?: string | null | undefined;
  imageSize?: ImageSize;
  cards: Array<CardData>;
  maxLength?: number;
  seeMoreText?: string;
};

type Props = {
  content: PublicPage;
  data: Data;
  blockId: string;
};

export function AmpCardsBlock({
  content,
  data: {
    headline,
    subheadline,
    imageSize,
    buttonText,
    buttonUrl,
    cards,
    maxLength = 3,
    seeMoreText,
  },
  blockId,
}: Props) {
  const pageUrl = getContentPageUrl({ content });
  const rowLength = Math.min(maxLength, 4);
  return (
    <CardContainer
      headline={headline}
      subheadline={subheadline}
      buttonText={buttonText}
      buttonUrl={buttonUrl}
    >
      {cards
        .slice(0, maxLength || cards.length)
        .map<ReactNode>((card, index) => (
          <Box // eslint-disable-next-line react/no-array-index-key
            key={index}
            width={[1, null, 1 / rowLength]}
            maxWidth={320}
            px={[0, null, gutterWidth / 2]}
            py={[gutterWidth / 2, null, 0]}
          >
            <Card maxLength={rowLength} card={card} imageSize={imageSize} />
          </Box>
        ))}
      {maxLength && cards.length > maxLength && (
        <Box mt={24}>
          <SecondaryButtonLink href={`/${pageUrl}#cms-block-${blockId}`}>
            {seeMoreText || getText('See All')}
          </SecondaryButtonLink>
        </Box>
      )}
    </CardContainer>
  );
}
